// Fiche d'un document : métadonnées (modifiables), historique des versions, revue et nouvelle version.
import { useRef, useState, type FormEvent, type ReactNode } from 'react';
import { Link, useParams } from 'react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { BookOpen, CalendarCheck, Check, FileUp, Pencil, Plus, X } from 'lucide-react';
import { api, messageErreur, ErreurApi } from '../../lib/api.ts';
import { useSession } from '../../lib/session.tsx';
import { date, dateHeure } from '../../lib/format.ts';
import {
  Alerte, Badge, Bouton, Champ, ChampChoix, ChampTexte, Chargement, Dialogue, EntetePage, EtatErreur, FilContexte, LienBouton, Panneau, Valeur,
  useConfirmation, useNotifier, type NoeudFil,
} from '../../components/ui.tsx';
import { BadgeStatutDocument } from '../../components/statuts.tsx';
import { LANGUES, TYPES_BIBLIOTHEQUE, TYPES_DOCUMENT, libelleType, tailleFichier, type Cadre, type Document, type Version } from './commun.tsx';

interface Fiche extends Document, Cadre { cree_par: string | null; versions: (Version & { importe_par: string | null })[] }

export function filDocument(doc: { id: string; title: string; scope: string }, cadre: Cadre, version?: string): NoeudFil[] {
  const tete: NoeudFil[] = doc.scope === 'dossier' && cadre.dossier
    ? [...(cadre.site ? [{ type: 'Site', libelle: cadre.site.name, to: `/sites/${cadre.site.id}` }] : []),
      { type: 'Dossier', libelle: cadre.dossier.reference, to: `/etudes/${cadre.dossier.id}` }]
    : doc.scope === 'site' && cadre.site
      ? [{ type: 'Site', libelle: cadre.site.name, to: `/sites/${cadre.site.id}` }]
      : [{ type: 'Documentation', libelle: 'Bibliothèque', to: '/documentation' }];
  return [...tete, { type: 'Document', libelle: doc.title, to: `/documentation/${doc.id}` }, ...(version ? [{ type: 'Version', libelle: version }] : [])];
}

export function PageDocument() {
  const { id } = useParams();
  const { peut } = useSession();
  const qc = useQueryClient();
  const notifier = useNotifier();
  const { confirmer, element: confirmation } = useConfirmation();
  const [edition, setEdition] = useState(false);
  const [nouvelleVersion, setNouvelleVersion] = useState(false);
  const [rejet, setRejet] = useState<Version | null>(null);
  const [occupe, setOccupe] = useState<string | null>(null);
  const q = useQuery({ queryKey: ['document', id], queryFn: () => api.get<Fiche>(`/api/documents/${id}`) });

  if (q.isLoading) return <Chargement lignes={6} />;
  if (q.isError || !q.data) return <EtatErreur erreur={q.error} reessayer={() => q.refetch()} />;
  const d = q.data;
  const courante = d.versions[0];
  const rafraichir = () => { qc.invalidateQueries({ queryKey: ['document', id] }); qc.invalidateQueries({ queryKey: ['documents'] }); qc.invalidateQueries({ queryKey: ['documents-couverture'] }); };

  const revue = async (v: Version, action: 'valider' | 'obsolete' | 'verifie', motif?: string) => {
    if (action === 'obsolete' && !(await confirmer(`Marquer la version ${v.version_label} comme obsolète ?`,
      'Elle restera consultable dans l’historique, mais ne sera plus proposée comme référence dans les recherches.', 'Marquer obsolète'))) return;
    setOccupe(`${v.id}-${action}`);
    try {
      await api.post(`/api/documents/versions/${v.id}/${action}`, motif ? { motif } : {});
      notifier(action === 'valider' ? 'Version validée.' : action === 'obsolete' ? 'Version marquée obsolète.' : 'Vérification enregistrée à la date du jour.');
      rafraichir();
    } catch (e) { notifier(messageErreur(e), true); } finally { setOccupe(null); }
  };

  const champs: [string, ReactNode][] = [
    ['Type', libelleType(d.doc_type)],
    ['Fabricant', <Valeur v={d.manufacturer} />],
    ['Gamme', <Valeur v={d.range_name} />],
    ['Références produit', d.product_refs.length ? <span className="doc-refs">{d.product_refs.map((r) => <span key={r} className="mono doc-ref">{r}</span>)}</span> : <span className="inconnu">Aucune</span>],
    ['Versions couvertes', <Valeur v={d.versions_covered} inconnu="Non précisées" />],
    ['Protocoles', <Valeur v={d.protocols} inconnu="Non précisés" />],
    ['Langue', <Valeur v={d.language ? LANGUES[d.language] ?? d.language : null} />],
    ['Région', <Valeur v={d.region} inconnu="Non précisée" />],
    ['Date de publication', <Valeur v={d.published_on ? date(d.published_on) : null} />],
    ['Provenance', <Valeur v={d.provenance} inconnu="Non renseignée" />],
    ['Droits d’utilisation', <Valeur v={d.rights} inconnu="Non renseignés" />],
    ['Importé par', <span>{d.cree_par ?? 'Inconnu'} — {date(d.created_at)}</span>],
  ];

  return (
    <div className="pile-l doc-module">
      <EntetePage fil={<FilContexte noeuds={filDocument(d, d)} />}
        titre={<span className="rangee" style={{ gap: 10 }}>{d.title}{courante && <BadgeStatutDocument statut={courante.status} />}{d.demo && <Badge ton="ambre">Démo</Badge>}</span>}
        sousTitre={[d.manufacturer, d.range_name, libelleType(d.doc_type)].filter(Boolean).join(' · ')}
        actions={<>
          {courante && <LienBouton to={`/documentation/lecteur/${courante.id}`} variante="principal" icone={<BookOpen aria-hidden />}>Lire la version {courante.version_label}</LienBouton>}
          {peut('doc.importer') && <Bouton icone={<Pencil aria-hidden />} onClick={() => setEdition(true)}>Modifier</Bouton>}
        </>} />

      {courante && courante.status !== 'valide' && (
        <Alerte ton="ambre" titre={courante.status === 'obsolete' ? 'Version la plus récente obsolète' : 'Version la plus récente non validée'}>
          Ne pas citer ce document comme référence sans vérification. {peut('doc.valider') ? 'Relisez-le puis validez-le ou rejetez-le.' : 'Un référent technique doit le valider.'}
        </Alerte>
      )}

      <div className="grille-2">
        <Panneau titre="Informations">
          <dl className="fiche">{champs.map(([k, v]) => <div key={k}><dt>{k}</dt><dd>{v}</dd></div>)}</dl>
        </Panneau>

        <Panneau serre titre="Historique des versions"
          actions={peut('doc.importer') && <Bouton compact icone={<Plus aria-hidden />} onClick={() => setNouvelleVersion(true)}>Nouvelle version</Bouton>}>
          <ol className="liste">
            {d.versions.map((v, i) => {
              const enCours = ['quarantaine', 'en_traitement'].includes(v.status);
              return (
                <li key={v.id} className="doc-version">
                  <div className="doc-version-tete">
                    <Link to={`/documentation/lecteur/${v.id}`} className="liste-element-titre">Version {v.version_label}</Link>
                    {i === 0 && <Badge ton="neutre" icone={false}>La plus récente</Badge>}
                    <BadgeStatutDocument statut={v.status} />
                  </div>
                  <div className="tres-petit secondaire doc-version-details">
                    <span>{v.file_name} · {tailleFichier(v.size_bytes)}{v.page_count ? ` · ${v.page_count} page${v.page_count > 1 ? 's' : ''}` : ''}</span>
                    <span>Collecté le {date(v.collected_at)}{v.importe_par ? ` par ${v.importe_par}` : ''}</span>
                    <span>Dernière vérification : {v.last_checked_at ? dateHeure(v.last_checked_at) : 'jamais'}</span>
                    {v.reviewed_at && <span>Revue le {dateHeure(v.reviewed_at)}{v.relecteur ? ` par ${v.relecteur}` : ''}</span>}
                  </div>
                  {v.status_detail && <p className="petit secondaire doc-version-detail">{v.status_detail}</p>}
                  {peut('doc.valider') && !enCours && (
                    <div className="groupe-btn doc-version-actions">
                      {['a_valider', 'rejete'].includes(v.status) && (
                        <Bouton compact variante="principal" icone={<Check aria-hidden />} chargement={occupe === `${v.id}-valider`} onClick={() => revue(v, 'valider')}>Valider</Bouton>
                      )}
                      {v.status !== 'rejete' && <Bouton compact icone={<X aria-hidden />} onClick={() => setRejet(v)}>Rejeter</Bouton>}
                      {v.status !== 'obsolete' && <Bouton compact onClick={() => revue(v, 'obsolete')}>Marquer obsolète</Bouton>}
                      <Bouton compact variante="fantome" icone={<CalendarCheck aria-hidden />} chargement={occupe === `${v.id}-verifie`} onClick={() => revue(v, 'verifie')}>Marquer vérifié</Bouton>
                    </div>
                  )}
                  {enCours && <p className="tres-petit discret">Lecture en cours : la revue sera possible à la fin du traitement.</p>}
                </li>
              );
            })}
          </ol>
        </Panneau>
      </div>

      {edition && <EditionDocument doc={d} fermer={() => setEdition(false)} enregistre={() => { setEdition(false); notifier('Document mis à jour.'); rafraichir(); }} />}
      {nouvelleVersion && <ImportVersion doc={d} fermer={() => setNouvelleVersion(false)} fini={() => { setNouvelleVersion(false); rafraichir(); }} />}
      {rejet && <DialogueRejet version={rejet} fermer={() => setRejet(null)} fini={() => { setRejet(null); notifier('Version rejetée.'); rafraichir(); }} />}
      {confirmation}
    </div>
  );
}

function DialogueRejet({ version, fermer, fini }: { version: Version; fermer: () => void; fini: () => void }) {
  const [motif, setMotif] = useState('');
  const [erreur, setErreur] = useState<string | null>(null);
  const [envoi, setEnvoi] = useState(false);
  const envoyer = async (e?: FormEvent) => {
    e?.preventDefault();
    if (!motif.trim()) { setErreur('Indiquez pourquoi ce document est rejeté.'); return; }
    setEnvoi(true);
    try { await api.post(`/api/documents/versions/${version.id}/rejeter`, { motif: motif.trim() }); fini(); }
    catch (ex) { setErreur(messageErreur(ex)); } finally { setEnvoi(false); }
  };
  return (
    <Dialogue titre={`Rejeter la version ${version.version_label}`} ouvert fermer={fermer}
      pied={<><Bouton onClick={fermer}>Annuler</Bouton><Bouton variante="danger" chargement={envoi} onClick={() => envoyer()}>Rejeter la version</Bouton></>}>
      <form onSubmit={envoyer} className="pile">
        <ChampTexte libelle="Motif du rejet" required value={motif} onChange={(e) => setMotif(e.target.value)} rows={3} erreur={erreur}
          placeholder="Ex. document d’une autre gamme, version périmée, source non autorisée…" />
      </form>
    </Dialogue>
  );
}

function EditionDocument({ doc, fermer, enregistre }: { doc: Fiche; fermer: () => void; enregistre: () => void }) {
  const [v, setV] = useState({
    title: doc.title, docType: doc.doc_type, manufacturer: doc.manufacturer ?? '', rangeName: doc.range_name ?? '',
    productRefs: doc.product_refs.join(', '), versionsCovered: doc.versions_covered ?? '', protocols: doc.protocols.join(', '),
    language: doc.language ?? '', region: doc.region ?? '', provenance: doc.provenance ?? '', rights: doc.rights ?? '',
    publishedOn: doc.published_on ? String(doc.published_on).slice(0, 10) : '',
  });
  const [erreurs, setErreurs] = useState<Record<string, string>>({});
  const [erreur, setErreur] = useState<string | null>(null);
  const [envoi, setEnvoi] = useState(false);
  const maj = (k: keyof typeof v) => (e: { target: { value: string } }) => setV((x) => ({ ...x, [k]: e.target.value }));
  const types = doc.scope === 'bibliotheque' ? TYPES_BIBLIOTHEQUE : Object.keys(TYPES_DOCUMENT);
  const envoyer = async (e?: FormEvent) => {
    e?.preventDefault();
    setEnvoi(true); setErreur(null); setErreurs({});
    try {
      await api.patch(`/api/documents/${doc.id}`, {
        ...v, productRefs: v.productRefs.split(/[,;\n]/).map((x) => x.trim()).filter(Boolean),
        protocols: v.protocols.split(',').map((x) => x.trim()).filter(Boolean), publishedOn: v.publishedOn || null,
      });
      enregistre();
    } catch (ex) {
      if (ex instanceof ErreurApi && Array.isArray(ex.details)) setErreurs(Object.fromEntries((ex.details as any[]).map((x) => [x.champ, x.message])));
      setErreur(messageErreur(ex));
    } finally { setEnvoi(false); }
  };
  return (
    <Dialogue titre="Modifier le document" ouvert fermer={fermer} large
      pied={<><Bouton onClick={fermer}>Annuler</Bouton><Bouton variante="principal" chargement={envoi} onClick={() => envoyer()}>Enregistrer</Bouton></>}>
      <form onSubmit={envoyer} className="pile">
        {erreur && <Alerte ton="danger">{erreur}</Alerte>}
        <div className="grille-form">
          <Champ libelle="Titre" value={v.title} onChange={maj('title')} erreur={erreurs.title} />
          <ChampChoix libelle="Type de document" value={v.docType} onChange={maj('docType')} options={types.map((t) => ({ valeur: t, libelle: TYPES_DOCUMENT[t] }))} />
          <Champ libelle="Fabricant" value={v.manufacturer} onChange={maj('manufacturer')} />
          <Champ libelle="Gamme" value={v.rangeName} onChange={maj('rangeName')} />
          <Champ libelle="Références produit" mono value={v.productRefs} onChange={maj('productRefs')} aide="Séparées par des virgules." />
          <Champ libelle="Versions couvertes" value={v.versionsCovered} onChange={maj('versionsCovered')} />
          <Champ libelle="Protocoles" value={v.protocols} onChange={maj('protocols')} aide="Séparés par des virgules." />
          <ChampChoix libelle="Langue" value={v.language} onChange={maj('language')} vide="Inconnue" options={Object.entries(LANGUES).map(([valeur, libelle]) => ({ valeur, libelle }))} />
          <Champ libelle="Région" value={v.region} onChange={maj('region')} />
          <Champ libelle="Date de publication" type="date" value={v.publishedOn} onChange={maj('publishedOn')} />
        </div>
        <ChampTexte libelle="Provenance" value={v.provenance} onChange={maj('provenance')} rows={2} erreur={erreurs.provenance} />
        <ChampTexte libelle="Droits d’utilisation" value={v.rights} onChange={maj('rights')} rows={2} erreur={erreurs.rights} />
      </form>
    </Dialogue>
  );
}

function ImportVersion({ doc, fermer, fini }: { doc: Fiche; fermer: () => void; fini: () => void }) {
  const notifier = useNotifier();
  const entree = useRef<HTMLInputElement>(null);
  const [fichier, setFichier] = useState<File | null>(null);
  const [libelle, setLibelle] = useState('');
  const [publication, setPublication] = useState('');
  const [erreur, setErreur] = useState<string | null>(null);
  const [envoi, setEnvoi] = useState(false);
  const envoyer = async () => {
    if (!fichier) { setErreur('Choisissez le fichier de la nouvelle version.'); return; }
    const fd = new FormData();
    fd.append('meta', JSON.stringify({ versionLabel: libelle.trim() || null, publishedOn: publication || null }));
    fd.append('fichier', fichier, fichier.name);
    setEnvoi(true); setErreur(null);
    try {
      const r = await api.envoyer<{ doublon: boolean; documentId: string }>(`/api/documents/${doc.id}/versions`, fd);
      if (r.doublon) notifier(r.documentId === doc.id ? 'Ce fichier est déjà une version de ce document : rien n’a été ajouté.' : 'Ce fichier est déjà présent dans la bibliothèque (autre document) : rien n’a été ajouté.', true);
      else notifier('Nouvelle version importée : lecture en cours. L’ancienne version est marquée obsolète.');
      fini();
    } catch (ex) { setErreur(messageErreur(ex)); } finally { setEnvoi(false); }
  };
  return (
    <Dialogue titre="Importer une nouvelle version" ouvert fermer={fermer}
      pied={<><Bouton onClick={fermer}>Annuler</Bouton><Bouton variante="principal" icone={<FileUp aria-hidden />} chargement={envoi} onClick={envoyer}>Importer la version</Bouton></>}>
      <div className="pile">
        <p className="petit secondaire">La version actuelle passera « obsolète » ; elle reste consultable dans l’historique. La nouvelle version devra être validée.</p>
        <div className="rangee">
          <Bouton icone={<Plus aria-hidden />} onClick={() => entree.current?.click()}>Choisir un fichier</Bouton>
          <span className="petit">{fichier ? `${fichier.name} (${tailleFichier(fichier.size)})` : 'Aucun fichier choisi'}</span>
          <input ref={entree} type="file" accept=".pdf,.png,.jpg,.jpeg,.webp,.xlsx,.csv,.txt" hidden
            onChange={(e) => { setFichier(e.target.files?.[0] ?? null); e.target.value = ''; }} />
        </div>
        <div className="grille-form">
          <Champ libelle="Libellé de version" value={libelle} onChange={(e) => setLibelle(e.target.value)} aide="Ex. « rév. D ». Par défaut : numéro suivant." />
          <Champ libelle="Date de publication" type="date" value={publication} onChange={(e) => setPublication(e.target.value)} />
        </div>
        {erreur && <Alerte ton="danger">{erreur}</Alerte>}
      </div>
    </Dialogue>
  );
}
